import React, { memo } from "react";
import { Stack, type BoxProps, type StackProps } from "@chakra-ui/react";

import WithHeading1 from "./WithHeading1";
import WithHeading2 from "./WithHeading2";
import WithHeading3 from "./WithHeading3";
import WithKicker from "./WithKicker";
import WithPageIntro from "./WithPageIntro";

export type PublicSectionHeaderLevel = 1 | 2 | 3;

export type PublicSectionHeaderProps = Omit<
  StackProps,
  "align" | "children" | "title"
> & {
  title: React.ReactNode;
  description?: React.ReactNode;
  kicker?: React.ReactNode;
  headingLevel?: PublicSectionHeaderLevel;
  align?: BoxProps["textAlign"];
  maxW?: BoxProps["maxW"];
  mb?: BoxProps["mb"];
};

function renderHeading(
  headingLevel: PublicSectionHeaderLevel,
  title: React.ReactNode,
  align: BoxProps["textAlign"],
) {
  if (headingLevel === 1) {
    return (
      <WithHeading1 textAlign={align} pt="0">
        {title}
      </WithHeading1>
    );
  }

  if (headingLevel === 3) {
    return (
      <WithHeading3 textAlign={align} pt="0">
        {title}
      </WithHeading3>
    );
  }

  return (
    <WithHeading2 textAlign={align} pt="0">
      {title}
    </WithHeading2>
  );
}

function mapTextAlignToStackAlign(
  value: BoxProps["textAlign"],
): StackProps["align"] {
  if (value === "center") {
    return "center";
  }

  if (value === "right" || value === "end") {
    return "flex-end";
  }

  return "stretch";
}

function getHeaderStackAlign(
  align: BoxProps["textAlign"],
): StackProps["align"] {
  if (Array.isArray(align)) {
    return align.map((value) =>
      value ? mapTextAlignToStackAlign(value) : value,
    ) as StackProps["align"];
  }

  if (align && typeof align === "object") {
    return Object.fromEntries(
      Object.entries(align).map(([breakpoint, value]) => [
        breakpoint,
        mapTextAlignToStackAlign(value),
      ]),
    ) as StackProps["align"];
  }

  return mapTextAlignToStackAlign(align);
}

function PublicSectionHeader({
  title,
  description,
  kicker,
  headingLevel = 2,
  align = { base: "center", md: "left" },
  maxW = "container.md",
  mb = { base: "6", md: "8" },
  ...stackProps
}: PublicSectionHeaderProps) {
  return (
    <Stack
      as="header"
      align={getHeaderStackAlign(align)}
      maxW={maxW}
      mb={mb}
      textAlign={align}
      spaceY="2"
      {...stackProps}
    >
      {kicker ? (
        <WithKicker textAlign={align} mt="0">
          {kicker}
        </WithKicker>
      ) : null}
      {renderHeading(headingLevel, title, align)}
      {description ? (
        <WithPageIntro mt="0" textAlign={align}>
          {description}
        </WithPageIntro>
      ) : null}
    </Stack>
  );
}

export default memo(PublicSectionHeader);
